<template>
  <div :class="namespaceClass()">
    <div :class="namespaceClass('scrollable')">
      <div :id="chartContainerId" :class="namespaceClass('chart')"></div>
      <div v-if="hasTableData" :class="namespaceClass('table')">
        <mds-table>
          <mds-tbody>
            <template v-for="(row, index) in convertedData.tableData">
              <mds-tr v-if="isHeaderRow(index)" :key="index" class="header-row">
                <mds-td
                  v-for="(col, i) in row"
                  :key="i"
                  class="cell"
                  :class="i === row.length - 1 ? ' last' : ''"
                  :title="index === 1 && col ? col : undefined"
                  :right-aligned="i !== row.length - 1"
                >
                  {{ col }}
                </mds-td>
              </mds-tr>
              <mds-tr v-else :key="index" class="row">
                <template v-for="(col, i) in row">
                  <mds-td
                    v-if="i !== row.length - 1"
                    :key="i"
                    :class="isItalic(index, i) ? 'cell italic' : 'cell'"
                  >
                    {{ formatNumberSymbol(formatter.number(col)) }}
                  </mds-td>
                  <mds-td v-else :key="i" class="cell last">
                    <span class="rect-icon" :style="{ backgroundColor: legendColor(index) }"></span>
                    <div :title="col" class="name">{{ col }}</div>
                  </mds-td>
                </template>
              </mds-tr>
            </template>
          </mds-tbody>
        </mds-table>
        <mds-table v-if="hasGrossReturnData">
          <mds-tbody>
            <template v-for="(row, index) in convertedData.grossReturnTableData">
              <mds-tr v-if="isHeaderRow(index)" :key="index" class="header-row">
                <mds-td
                  v-for="(col, i) in row"
                  :key="i"
                  class="cell"
                  :class="i === row.length - 1 ? ' last' : ''"
                  :title="index === 1 && col ? col : undefined"
                >
                  {{ col }}
                </mds-td>
              </mds-tr>
              <mds-tr v-else :key="index" class="row">
                <template v-for="(col, i) in row">
                  <mds-td
                    v-if="i !== row.length - 1"
                    :key="i"
                    :class="isItalic(index, i) ? 'cell italic' : 'cell'"
                  >
                    {{ formatNumberSymbol(formatter.number(col)) }}
                  </mds-td>
                  <mds-td v-else :key="i" class="cell last">
                    <span class="rect-icon"></span>
                    <div :title="col" class="name">{{ col }}</div>
                  </mds-td>
                </template>
              </mds-tr>
            </template>
          </mds-tbody>
        </mds-table>
      </div>
    </div>
    <template v-if="showXScroll">
      <div :class="namespaceClass('__fixedAxis')">
        <svg width="100%" height="100%">
          <g></g>
        </svg>
      </div>
      <div :class="namespaceClass('__fixedTable')">
        <mds-table class="mds-data-table__fixed">
          <mds-tbody>
            <template v-for="(row, index) in fixedTableData">
              <mds-tr v-if="isHeaderRow(index)" :key="index" class="header-row">
                <mds-td
                  v-for="(col, i) in row"
                  :key="i"
                  class="cell"
                  :title="index === 1 && col ? col : undefined"
                >
                  {{ col }}
                </mds-td>
              </mds-tr>
              <mds-tr v-else :key="index" class="row">
                <template v-for="(col, i) in row">
                  <mds-td v-if="i !== row.length - 1" :key="i" class="cell">{{
                    formatNumberSymbol(formatter.number(col))
                  }}</mds-td>
                  <mds-td v-else :key="i" class="cell last">
                    <span class="rect-icon" :style="{ backgroundColor: legendColor(index) }"></span>
                    <div :title="col" class="name">{{ col }}</div>
                  </mds-td>
                </template>
              </mds-tr>
            </template>
          </mds-tbody>
        </mds-table>
        <mds-table v-if="hasGrossReturnData" class="mds-data-table__fixed">
          <mds-tbody>
            <template v-for="(row, index) in fixedGrossReturnTableData">
              <mds-tr v-if="isHeaderRow(index)" :key="index" class="header-row">
                <mds-td
                  v-for="(col, i) in row"
                  :key="i"
                  class="cell"
                  :title="index === 1 && col ? col : undefined"
                >
                  {{ col }}
                </mds-td>
              </mds-tr>
              <mds-tr v-else :key="index" class="row">
                <template v-for="(col, i) in row">
                  <mds-td v-if="i !== row.length - 1" :key="i" class="cell">{{
                    formatNumberSymbol(formatter.number(col))
                  }}</mds-td>
                  <mds-td v-else :key="i" class="cell last">
                    <span class="rect-icon"></span>
                    <div :title="col" class="name">{{ col }}</div>
                  </mds-td>
                </template>
              </mds-tr>
            </template>
          </mds-tbody>
        </mds-table>
      </div>
    </template>
  </div>
</template>

<script>
import d3 from 'd3';
import Core from 'mbc/dist/mbc-core';
import ResizeObserver from 'resize-observer-polyfill';
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsTable, MdsTbody, MdsTr, MdsTd } from '@mds/data-table';
import HypoVueBaseMixin from 'hypo-vue-base-mixin';
import defaultConfig from './_config';
import HypoReturnBarChart from './extends/hypo-return-bar-chart';

const UID = Core.Utils.string.UID;

export default {
  name: 'hypo-return-bar-chart',
  components: {
    MdsTable,
    MdsTbody,
    MdsTr,
    MdsTd,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin, HypoVueBaseMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    config: {
      type: [String, Object],
      default: () => {},
    },
    colors: [Array, String],
    modelData: {
      type: [String, Array],
      default() {
        return [];
      },
    },
    grossReturn: {
      type: [String, Array],
      default() {
        return [];
      },
    },
    showReturnCategory: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultConfig: {
        intlNamespace: this.mwcId,
        ...defaultConfig,
      },
      showXScroll: false,
    };
  },
  computed: {
    chartContainerId() {
      return `return-bar-chart-container${UID()}`;
    },
    configSettings() {
      return this.getComponentConfig().settings;
    },
    chartColors() {
      if (this.colors) {
        return typeof this.colors === 'string' ? JSON.parse(this.colors) : this.colors;
      }
      return this.configSettings.barChartSetting.colors;
    },
    convertedData() {
      // convert model data to chart data and table data.
      const parsedData =
        typeof this.modelData === 'string' ? JSON.parse(this.modelData) : this.modelData;
      const grossReturn =
        typeof this.grossReturn === 'string' ? JSON.parse(this.grossReturn) : this.grossReturn;
      const hasGrossReturn = grossReturn.length > 0;

      const { date, value, id } = this.configSettings.keyMapping;
      const xDomain = [];
      const dataMap = {};

      parsedData.forEach(item => {
        const dataObj = {};
        item.data.forEach(i => {
          if (xDomain.indexOf(i[date]) === -1) {
            xDomain.push(i[date]);
          }
          dataObj[i[date]] = i[value];
        });
        dataMap[item[id]] = dataObj;
      });

      const y1AxisData = [];
      parsedData.forEach(item => {
        const values = xDomain.map(x => {
          return dataMap[item[id]][x] !== undefined ? dataMap[item[id]][x] : null;
        });
        y1AxisData.push({
          name: item.name,
          values,
        });
      });

      const barData = {
        xDomain,
        y1AxisData,
      };

      const tableData = [];
      const grossReturnTableData = [];
      let netReturnLabel = '';
      let grossReturnLabel = '';
      if (this.options.showReturnCategory) {
        netReturnLabel = this.translate('netReturn');
        grossReturnLabel = this.translate('grossReturn');
      }
      tableData.push(xDomain.concat(netReturnLabel));
      const monthlabels = this.getMonthLabels(parsedData, xDomain);
      if (this.settings.showMonthTag) {
        tableData.push(monthlabels.concat(''));
      }

      if (hasGrossReturn) {
        grossReturnTableData.push(xDomain.concat(grossReturnLabel));
        if (this.settings.showMonthTag) {
          grossReturnTableData.push(monthlabels.concat(''));
        }
      }

      grossReturn.forEach(item => {
        const row = [];
        xDomain.forEach(t => {
          const data = item.data.find(d => d[date] === t);
          const returnValue = data && typeof data[value] !== 'undefined' ? data[value] : null;
          row.push(returnValue);
        });
        row.push(item[id]);
        grossReturnTableData.push(row);
      });

      y1AxisData.map(item => {
        tableData.push(item.values.concat(item.name));
      });
      return {
        barData,
        tableData,
        grossReturnTableData,
      };
    },
    hasTableData() {
      const { tableData } = this.convertedData;
      return tableData.length > 0 && tableData[0].length > 1;
    },
    hasGrossReturnData() {
      const { grossReturnTableData } = this.convertedData;
      return grossReturnTableData.length > 0 && grossReturnTableData[0].length > 1;
    },
    fixedTableData() {
      return this.convertedData.tableData.map(cols =>
        cols.filter((col, i) => {
          return i === cols.length - 1;
        })
      );
    },
    fixedGrossReturnTableData() {
      return this.convertedData.grossReturnTableData.map(cols =>
        cols.filter((col, i) => {
          return i === cols.length - 1;
        })
      );
    },
  },
  watch: {
    modelData() {
      this.drawChart();
    },
    chartColors() {
      this.drawChart();
    },
  },
  mounted() {
    this.drawChart();
    const myObserver = new ResizeObserver(entries => {
      entries.map(() => {
        this.handleResize();
      });
    });
    myObserver.observe(this.$el);
  },
  methods: {
    isItalic(rowIndex, colIndex) {
      const dataInitalRowIndex = this.convertedData.tableData.length - this.modelData.length;
      const italicMatrix = this.modelData.map(item => {
        return item.data.map(dataItem => {
          return dataItem.italic;
        });
      });
      return italicMatrix[rowIndex - dataInitalRowIndex][colIndex];
    },
    isHeaderRow(index) {
      if (this.settings.showMonthTag) {
        return index === 0 || index === 1;
      }
      return index === 0;
    },
    legendColor(index) {
      const len = this.chartColors.length;
      if (this.settings.showMonthTag) {
        return this.chartColors[(index - 2) % len];
      }
      return this.chartColors[(index - 1) % len];
    },
    getMonthLabels(dataSource, domain) {
      const monthlabels = [];
      if (dataSource.length > 0) {
        const startDates = dataSource.map(i => new Date(i.startDate));
        const endDates = dataSource.map(i => new Date(i.endDate));
        const minStartDate = d3.min(startDates);
        const maxEndDate = d3.max(endDates);
        domain.forEach((item, i, arr) => {
          if (i === arr.length - 1) {
            monthlabels.push(this.getEndPeriodLabel(maxEndDate));
          } else if (i === 0) {
            monthlabels.push(this.getStartPeriodLabel(minStartDate));
          } else {
            monthlabels.push('');
          }
        });
      }
      return monthlabels;
    },
    getStartPeriodLabel(date) {
      let label;
      const month = date.getMonth();
      const year = date.getFullYear();
      const yearEndDate = new Date(Date.UTC(year, 11, 31, 0, 0, 0));
      if (month === 11) {
        label = `${this.formatter.format(yearEndDate, 'mm')}`;
      } else {
        label = `${this.formatter.format(date, 'mm')}${this.getEnDash()}${this.formatter.format(
          yearEndDate,
          'mm'
        )}`;
      }
      return label;
    },
    getEndPeriodLabel(date) {
      let label;
      const month = date.getMonth();
      const year = date.getFullYear();
      const yearStartDate = new Date(Date.UTC(year, 0, 31, 0, 0, 0));
      if (month === 0) {
        label = `${this.formatter.format(date, 'mm')}`;
      } else {
        label = `${this.formatter.format(
          yearStartDate,
          'mm'
        )}${this.getEnDash()}${this.formatter.format(date, 'mm')}`;
      }
      return label;
    },
    drawChart() {
      this.chartEl = d3.select(this.$el);
      const container = d3.select(`#${this.chartContainerId}`);
      container.selectAll('*').remove();
      container.attr({
        class: this.namespaceClass('chart'),
        id: this.chartContainerId,
      });
      let chartData = this.convertedData.barData;
      if (chartData.y1AxisData.length === 0) {
        const emptyChartSetting = this.configSettings.emptyChartSetting;
        this.chart = new HypoReturnBarChart({
          ...this.configSettings.emptyChartSetting,
        });
        chartData = {
          xDomain: emptyChartSetting.xDomain,
          y1AxisData: [],
        };
      } else {
        this.chart = new HypoReturnBarChart({
          ...this.configSettings.barChartSetting,
          colors: this.chartColors,
        });
        const self = this;
        this.chart.on('afterDraw', function() {
          const xScroll = self.chart.get('xScroll');
          if (xScroll) {
            self.showXScroll = true;
            self.$nextTick(() => {
              self.setTableSize();
              self.drawFixedYAxes();
              self.setFixedTableSize();
              self.scrollToRight();
            });
          } else {
            self.showXScroll = false;
            self.$nextTick(() => {
              self.setTableSize();
            });
          }
        });
      }
      container.datum(chartData).call(this.chart.init());
    },
    scrollToRight() {
      const scrollContainer = this.chartEl.select(`.${this.namespaceClass('scrollable')}`).node();
      scrollContainer.scrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    },
    setTableSize() {
      const yTickSize = this.chart.get('yTickSize');
      const yPadding = this.chart.get('yPadding');
      const xScroll = this.chart.get('xScroll');
      if (!xScroll) {
        this.chartEl.select(`.${this.namespaceClass('table')}`).style({
          width: '',
          'max-width': '',
          'padding-right': `${yPadding}px`,
        });
      } else {
        const svgWidth = this.chart.get('svgWidth');
        const tableWidth = svgWidth - yPadding;
        this.chartEl.select(`.${this.namespaceClass('table')}`).style({
          width: `${tableWidth}px`,
          'max-width': `${tableWidth}px`,
          'padding-right': '',
        });
      }
      const table = this.chartEl.select(`.${this.namespaceClass('table')}`);
      table.selectAll('.cell:not(:last-child)').style({
        width: '',
      });
      table.selectAll('.cell:last-child').style({
        width: `${yTickSize + yPadding}px`,
      });
    },
    drawFixedYAxes() {
      const yPadding = this.chart.get('yPadding');
      const yTickSize = this.chart.get('yTickSize');
      const y1Axis = this.chartEl.select(`#${this.chartContainerId} .mbc-axis-right`).node();
      const fixedAxisContainer = this.chartEl
        .select(`.${this.namespaceClass('__fixedAxis')}`)
        .style({
          width: `${yPadding + yTickSize + yPadding}px`,
        });
      const axesGroup = fixedAxisContainer
        .select('svg')
        .select('g')
        .attr('transform', `translate(${yPadding},5)`);
      axesGroup.selectAll('g.mbc-axis').remove();
      axesGroup.node().appendChild(y1Axis.cloneNode(true));
      axesGroup.select('.mbc-axis-right').attr('transform', `translate(0,0)`);
    },
    setFixedTableSize() {
      const yPadding = this.chart.get('yPadding');
      const yTickSize = this.chart.get('yTickSize');
      this.chartEl.select(`.${this.namespaceClass('__fixedTable')}`).style({
        width: `${yPadding + yTickSize + yPadding}px`,
        'padding-right': `${yPadding}px`,
      });
    },
    handleResize() {
      if (
        document.querySelector(`#${this.chartContainerId}`) &&
        this.chart.get('canvas') !== null
      ) {
        this.chart.update();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@mds/constants';
@import '~@mds/typography';
@import './styles/hypo-return-bar-chart';

.italic {
  font-style: italic;
}
</style>
